import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { GraduationCap, MessageSquare, Trophy, MapPin, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Design tokens - matches the mundalo.com neon green / dark navy system
const COLOR = {
  navy: '#0a1628',
  navySoft: '#1e2a3a',
  navyMuted: '#4a5568',
  lime: '#2CA8A0',
  limeHover: '#2CA8A0',
  border: '#e5e7eb',
  pageBg: '#ffffff',
  panelBg: '#f8fafc',
};

const FEATURES = [
  {
    icon: GraduationCap,
    title: 'Classroom',
    description:
      'Structured video modules covering visas, housing, banking, healthcare, and more. Learn at your own pace.',
  },
  {
    icon: MessageSquare,
    title: 'Community Feed',
    description:
      'Topic channels for Visas, Housing, Banking, Healthcare, and Moving Tips. Real answers from real people.',
  },
  {
    icon: Trophy,
    title: 'Leaderboard',
    description:
      'See the most active members, track your progress, and climb the ranks as you learn and contribute.',
  },
  {
    icon: MapPin,
    title: 'City Guides',
    description:
      'Deep resources for Mexico City, Tulum, Playa del Carmen, Mérida, Puerto Vallarta, and San Miguel de Allende.',
  },
];

const RelocationCommunity = () => {
  const [stats, setStats] = useState({ members: null, discussions: null, cities: 6 });

  useEffect(() => {
    fetch(`${API_URL}/api/community/public-stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStats((p) => ({ ...p, ...d })))
      .catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: COLOR.pageBg, color: COLOR.navy }}
      data-testid="community-hub-page"
    >
      <SEO
        title="Community Hub | Mundalo"
        description="The community for Americans making their move to Mexico. Ask questions, share experiences, and get real answers."
      />
      <Header currentPage="relocation" />

      {/* =============== SECTION 1 - HERO =============== */}
      <section
        className="w-full"
        style={{ backgroundColor: COLOR.pageBg }}
        data-testid="community-hero"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24 text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-4 break-words"
            style={{ color: COLOR.navy }}
            data-testid="community-hero-headline"
          >
            Settle with like-minded people.
            <br />
            Learn, connect, and move smarter.
          </h1>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto mb-8 leading-relaxed"
            style={{ color: COLOR.navyMuted }}
            data-testid="community-hero-subheading"
          >
            The community for Americans making their move to Mexico. Ask
            questions, share experiences, and get real answers from people
            doing exactly what you are doing.
          </p>
          <a
            href="/start-trial"
            className="inline-flex items-center gap-2 font-semibold px-8 py-4 text-base min-h-[48px] transition rounded-sm"
            style={{ backgroundColor: COLOR.lime, color: COLOR.navy }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = COLOR.limeHover)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = COLOR.lime)}
            data-testid="community-hero-cta"
          >
            Start free - 15 days
            <ArrowRight className="w-4 h-4" />
          </a>
          <p
            className="text-sm mt-4"
            style={{ color: COLOR.navyMuted }}
            data-testid="community-hero-no-cc"
          >
            Card at signup. Cancel anytime from your portal.
          </p>
        </div>
      </section>

      {/* =============== SECTION 2 - SOCIAL PROOF BAR =============== */}
      <section
        className="w-full border-y"
        style={{ backgroundColor: COLOR.panelBg, borderColor: COLOR.border }}
        data-testid="community-stats-bar"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-4 text-center">
            <Stat label="Members" value={stats.members} testId="stat-members" />
            <Stat label="Discussions" value={stats.discussions} testId="stat-discussions" />
            <Stat label="Cities covered" value={stats.cities} testId="stat-cities" />
          </div>
        </div>
      </section>

      {/* =============== SECTION 3 - WHAT YOU GET =============== */}
      <section
        className="w-full"
        style={{ backgroundColor: COLOR.pageBg }}
        data-testid="community-features"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <h2
            className="text-3xl sm:text-4xl font-light text-center mb-12"
            style={{ color: COLOR.navy }}
            data-testid="community-features-heading"
          >
            What you get
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="border rounded-sm p-6 sm:p-8 transition-all"
                  style={{
                    borderColor: COLOR.border,
                    backgroundColor: COLOR.pageBg,
                    boxShadow: '0 1px 3px rgba(10,22,40,0.04)',
                  }}
                  data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-5 rounded-sm"
                    style={{ backgroundColor: `${COLOR.lime}22` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: COLOR.lime, strokeWidth: 2.25 }} />
                  </div>
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: COLOR.navy }}
                  >
                    {f.title}
                  </h3>
                  <p className="text-sm sm:text-base leading-relaxed" style={{ color: COLOR.navyMuted }}>
                    {f.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =============== SECTION 4 - CTA BANNER =============== */}
      <section
        className="w-full"
        style={{ backgroundColor: COLOR.lime }}
        data-testid="community-cta-banner"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-20 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4"
            style={{ color: COLOR.navy }}
            data-testid="community-cta-headline"
          >
            Ready to find your people?
          </h2>
          <p
            className="text-base sm:text-lg mb-8"
            style={{ color: COLOR.navy }}
            data-testid="community-cta-subheading"
          >
            Start free for 15 days, then $9.99/month. Cancel anytime.
          </p>
          <a
            href="/start-trial"
            className="inline-flex items-center gap-2 font-semibold px-8 py-4 text-base min-h-[48px] transition rounded-sm"
            style={{ backgroundColor: COLOR.navy, color: '#ffffff' }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = COLOR.navySoft)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = COLOR.navy)}
            data-testid="community-cta-button"
          >
            Join the community free
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div
      className="text-3xl sm:text-4xl font-light mb-1"
      style={{ color: COLOR.navy }}
      data-testid={`${testId}-value`}
    >
      {value === null ? '…' : value.toLocaleString('en-US', { timeZone: 'America/Chicago' })}
    </div>
    <div
      className="text-sm uppercase tracking-wider"
      style={{ color: COLOR.navyMuted }}
    >
      {label}
    </div>
  </div>
);

export default RelocationCommunity;
